import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Series Loading Progress Bar
 *
 * Shows prefetch progress for a series as a thin bar at the bottom of thumbnails.
 * Green fill grows left-to-right as images load.
 * Uses polling since callback subscription has timing issues with reset().
 */
import { prefetchManager } from '@/lib/cornerstone/prefetchManager';
import { useEffect, useReducer } from 'react';
const INITIAL_STATE = {
    registered: false,
    progress: 0,
    complete: false,
    lookahead: false,
};
function loadingReducer(_state, action) {
    switch (action.type) {
        case 'UPDATE':
            return {
                registered: true,
                progress: action.progress,
                complete: action.complete,
                lookahead: action.lookahead,
            };
        case 'RESET':
            return INITIAL_STATE;
    }
}
export function SeriesLoadingBar({ seriesUID }) {
    const [state, dispatch] = useReducer(loadingReducer, INITIAL_STATE);
    useEffect(() => {
        const checkStatus = () => {
            const status = prefetchManager.getStatus(seriesUID);
            if (status) {
                const pct = status.total > 0 ? (status.loaded / status.total) * 100 : 0;
                dispatch({
                    type: 'UPDATE',
                    progress: pct,
                    complete: status.complete,
                    lookahead: prefetchManager.isLookahead(seriesUID),
                });
            }
            else {
                dispatch({ type: 'RESET' });
            }
        };
        checkStatus();
        const interval = setInterval(checkStatus, 500);
        return () => clearInterval(interval);
    }, [seriesUID]);
    const { registered, progress, complete, lookahead } = state;
    if (!registered) {
        return null;
    }
    // Fully loaded - thin solid green line
    if (complete) {
        return (_jsx("div", { className: "pointer-events-none absolute right-0 bottom-0 left-0 h-0.5 bg-green-500" }));
    }
    // Loading - progress bar. Open/active series load with a bright green bar;
    // background Tier 2 look-ahead uses a thinner, dimmer sky bar so it reads as
    // "prefetching in the background" rather than "this is the series loading".
    return (_jsx("div", { className: `pointer-events-none absolute right-0 bottom-0 left-0 bg-gray-700/50 ${lookahead ? 'h-0.5' : 'h-1'}`, children: _jsx("div", { className: `h-full transition-all duration-150 ease-out ${lookahead ? 'bg-sky-500/50' : 'bg-green-500'}`, style: { width: `${progress}%` } }) }));
}
